/**
 * Display labels for sites, locations and bins on the Locations list and the site modal. Pure,
 * so the "bins key off the bin" rules are testable without rendering anything.
 */
import type { Owner, SiteType, StorageBin } from '../types';
import { TYPE_LABEL } from '../types';
import { formatDateTime } from '@/utils/displayDateTime';

// V3-041: the page is Material Locations at /material-locations (the menu label, the page title
// and the route match, NAV / V3-042). The old /locations routes redirect here (App.tsx).
export const LOCATIONS_PATH = '/material-locations';
/** The route the page used to live at; every /locations… URL redirects to LOCATIONS_PATH. */
export const LEGACY_LOCATIONS_PATH = '/locations';
export const LEDGER_PATH = `${LOCATIONS_PATH}/ledger`;
export const COUNTS_PATH = `${LOCATIONS_PATH}/counts`;
export const CATALOGUE_PATH = `${LOCATIONS_PATH}/catalogue`;

export const countPath = (id: string) => `${COUNTS_PATH}/${encodeURIComponent(id)}`;

/** The ledger narrowed to a site, or to one bin at it: where "History" links go. */
export function ledgerPath(filter: { siteId?: string; binId?: string }): string {
  const params = new URLSearchParams();
  if (filter.siteId) params.set('siteId', filter.siteId);
  if (filter.binId) params.set('binId', filter.binId);
  const query = params.toString();
  return query ? `${LEDGER_PATH}?${query}` : LEDGER_PATH;
}

const KIND_LABEL: Record<string, string> = {
  STOCKPILE: 'Stockpile',
  SHED: 'Shed',
  FUEL_TANK: 'Fuel tank',
  BLADDER: 'Bladder',
  RACK: 'Rack',
  CAGE: 'Cage',
  SEA_CAN: 'Sea can',
  STORE_ROOM: 'Store room',
  YARD_AREA: 'Yard area',
};

/** "FUEL_TANK" -> "Fuel tank". Unknown values degrade to a readable sentence-case form. */
export function locationKindLabel(kind: string): string {
  if (KIND_LABEL[kind]) return KIND_LABEL[kind];
  const words = kind.toLowerCase().replace(/_/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Location';
}

/** "103L (TREATED 9.5mm Winter Sand)", or whichever half exists, or null for an empty bin. */
export function binMaterialLabel(bin: Pick<StorageBin, 'material'>): string | null {
  const material = bin.material;
  if (!material) return null;
  const code = material.code?.trim();
  const description = material.description?.trim();
  if (code && description && code !== description) return `${code} (${description})`;
  return code || description || null;
}

export function siteEyebrow(type: SiteType): string {
  return `Materials · ${TYPE_LABEL[type]}`;
}

export function siteTitle(site: { name: string; number: string | null }): string {
  return site.number ? `${site.name} #${site.number}` : site.name;
}

/**
 * Meta line under the site header, matching the prototype: yards and pits both read
 * "Name (Pit #number)". Rockfall sites omit the Pit badge when they have no number.
 */
export function siteMetaLabel(site: { name: string; number: string | null; type: SiteType }): string {
  if (site.number) return `${site.name} (Pit #${site.number})`;
  return site.name;
}

/**
 * "Last count 2026-06-11 8:00 AM" (V3-023 / FMT-01, FMT-02: the shared formatter). Returns null
 * when there is no count.
 */
export function formatLastCount(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return `Last count ${formatDateTime(date)}`;
}

export function binCountLabel(count: number): string {
  return count === 1 ? '1 bin' : `${count} bins`;
}

export type OwnerTone = 'info' | 'primary' | 'muted';

/** MoTI blue, DRM brand blue, everyone else neutral, as in the prototype. */
export function ownerTone(owner: Owner | string | null): OwnerTone {
  if (!owner) return 'muted';
  if (owner.startsWith('MoTI')) return 'info';
  if (owner.startsWith('DRM')) return 'primary';
  return 'muted';
}

export function googleMapsUrl(latitude: number, longitude: number): string {
  return `https://www.google.com/maps?q=${latitude},${longitude}`;
}
